<script>
    import field from './ib-field.vue';
    import $device from '../../prototype/device';
    import $locale from '../../prototype/locale';

    const haveInputMode = $device.hasKeyboard || 'inputMode' in HTMLInputElement.prototype;
    const zwnj = '\u200c';


    /**
     * A derivative of [ib-field] that restricts input to numeric and emits `Number` values. On
     * mobile devices, a numeric keyboard will be displayed, with decimal input if permitted by
     * `step` or `decimal`. Older mobile devices (for example iOS earlier than 12.3) will not
     * perform key filtering.
     */
    export default {
        name: 'ib-field-numeric',
        mixins: [field],

        props: {
            /**
             * Current value.
             */
            value: {
                type: Number,
                default: undefined
            },
            /**
             * Minimum legal value.
             */
            min: {
                type: Number,
                default: undefined
            },
            /**
             * Maximum legal value.
             */
            max: {
                type: Number,
                default: undefined
            },
            /**
             * Stepping interval for legal values. If defined with a decimal value, decimal
             * entry will be allowed on the field with enough decimal places to accommodate
             * `step`.
             */
            step: Number,
            /**
             * If `true`, format and parse values as percentages; this means displaying a percent
             * symbol in the locale appropriate position and displaying the value multiplied by
             * 100. For example, entering `5` will emit `0.05` and will fail a test of `:step="1"`.
             */
            percent: Boolean,
            /**
             * If `true`, allow decimal entry. If defined without `step`, no limit on decimal
             * places will be imposed.
             */
            decimal: Boolean,
            /**
             * If `true`, automatically right pad values with `0` to force the number of decimal places to
             * the amount required for `step`. For example, if `step` is `0.01` and a user enters `1.5` and
             * tabs out of the field, the value will be replaced with `1.50`.
             */
            fixed: Boolean,
            /**
             * Writing system. Only `latn` is currently supported.
             */
            script: {
                type: String,
                default: 'latn'
            },
            /**
             * Custom error string for value too low
             */
            errorUnderflow: String,
            /**
             * Custom error string for value too high
             */
            errorOverflow: String
        },

        data() {
            // Making the separator reactive causes more problems than it solves.
            return {
                lcurrent: $locale.ncurrent || $locale.current,
                ldecimal: $locale.number.decimal,
                validity: null,
                warnto: null,
                numeric: true,
                which: 0,
                saveStart: 0,
                saveEnd: 0,
                saveValue: ''
            };
        },

        computed: {
            _iclass() {
                return {
                    numeric: true
                };
            },

            /** Generate a function that returns `true` if the current value is not a multiple of `step` */
            stepInvalid() {
                const step = this.step;
                return step
                    ? value => Math.round(value / step) / (1 / step) !== value   // avoids precision issues
                    : value => false;
            },

            bind() {
                return {
                    type: 'text',
                    inputmode: !haveInputMode || ($device.ios && !(this.min >= 0)) ? null : this.decimal || this.fplaces ? 'decimal' : 'numeric',
                    pattern: this.tpattern,
                    autocomplete: this.autocomplete
                };
            },

            formatter() {
                return new Intl.NumberFormat(this.lcurrent, {
                    numberingSystem: 'latn',
                    useGrouping: false,
                    minimumFractionDigits: this.fixed ? this.fplaces : 0,
                    maximumFractionDigits: this.fixed && this.fplaces != null ? this.fplaces : 20,
                    style: this.percent ? 'percent' : undefined
                });
            },

            /** Convert stored value to displayed value */
            format() {
                return this.percent
                    ? value => isNaN(parseFloat(value)) ? null : this.formatter.format(value).replace(/\u200e|\u200f/g, '').replace(/\s*%/, '')
                    : value => isNaN(parseFloat(value)) ? null : this.formatter.format(value).replace(/\u200e|\u200f/g, '');
            },

            /** Convert displayed value to stored value */
            parse() {
                return this.ldecimal == '.'
                    ? value => +this.adjust(value)
                    : value => +this.adjust(value.replace(this.ldecimal, '.'));
            },

            /** Maximum fractional (mantissa) decimal places */
            fplaces() {
                return this.step == null ? undefined : Math.max(0, this.step.toString().replace(/^-?\d*\.?|0+$/g, '').length - (this.percent ? 2 : 0));
            },

            /** Maximum integer (characteristic) places */
            iplaces() {
                const max = Math.max(Math.abs(this.min == null ? -1e12 : this.min), Math.abs(this.max == null ? 1e12 : this.max));
                return (Math.log10(max) | 0) + 1 + (this.percent ? 2 : 0);
            },

            tpattern() {
                return (this.min >= 0 ? '' : '-?') + '\\d{0,' + this.iplaces + '}' + (this.decimal || this.fplaces ? '(\\' + this.ldecimal + '\\d' + (this.fplaces ? '{0,' + this.fplaces + '})?' : '*)?') : '');
            },

            rpattern() {
                return RegExp('^' + this.tpattern + '$');
            },

            /** If percent mode, divide value by 100 as a string operation for precision safety, otherwise pass through */
            adjust() {
                return this.percent
                    ? value => value.replace(/(\d{0,2})(\.|$)/, (a,b) => '.' + ('00' + b).slice(-2))
                    : value => value;
            },

            _prefix() {
                return this.percent && this.$rtl ? $locale.number.percentSign : this.prefix != null && this.prefix != '' ? this.prefix : zwnj;
            },

            _suffix() {
                return this.percent && this.$rtl ? $locale.number.percentSign : null;
            }
        },

        watch: {
            min: 'input',
            max: 'input',
            step: 'input'
        },

        created() {
            if (this.step != null && !(this.step >= 1e-9 && this.step <= 1e9)) {
                throw new RangeError('step out of range');
            }
        },

        mounted() {
            // Display alphanumeric keyboard on devices that do not support inputmode
            // or on iOS when negative numbers are needed
            if (!haveInputMode || ($device.ios && !(this.min >= 0))) {
                const el = this.$refs._in;
                el.addEventListener('touchstart', () => {
                    if (document.activeElement !== el && el.getAttribute('type') == 'text') {
                        this.saveState();
                        el.setAttribute('type', 'number');
                    }
                });
                el.addEventListener('focus', () => {
                    if (el.getAttribute('type') != 'text') {
                        el.setAttribute('type', 'text');
                        this.restoreState();
                    }
                });
            }
            this.saveState();
        },

        methods: {
            input() {
                this._sync();

                const input = this.$refs._in;
                const iplaces = this.iplaces;
                const decimal = this.ldecimal;

                // Mobile autoselect: handle as replace all
                if (this.mselect && this.saveValue != null) {
                    input.value = input.value.slice(this.saveStart, (this.saveEnd - this.saveStart) - (this.saveValue.length - input.value.length));
                }

                // Replace period separator with local separator
                if (decimal !== '.' && input.value.includes('.')) {
                    const start = input.selectionStart;
                    const end = input.selectionEnd;
                    input.value = input.value.replace('.', decimal);
                    input.selectionStart = start;
                    input.selectionEnd = end;
                }

                if (!this.rpattern.test(input.value)) {
                    this._warn();
                    this.restoreState();
                } else {
                    // Auto-insert separator
                    if (this.fplaces &&
                        this.which != 8 &&
                        this.which != 46 &&
                        !input.value.includes(decimal) &&
                        input.selectionEnd == input.value.length) {
                        const value = this.parse(input.value);
                        if ((value > 0 && value <= this.max && value * 10 > this.max) ||
                            (value < 0 && value >= this.min && value * 10 < this.min)) {
                            input.value += decimal;
                        }
                    }
                    if (this.mselect) {
                        this._selectoff(true);
                    }
                    this.empty = !input.value;
                    this.validate();
                    this._emit(this.toValue());
                    this.saveState();
                }
            },

            /**
             * Increase the value to the next higher amount allowed by `step`. If `step` is undefined, do nothing.
             */
            increment() {
                const step = this.step;
                if (step) {
                    let value = this.parse(this.$refs._in.value);
                    value = (Math.floor(value / step + 1e-12) + 1) / (1 / step);
                    if (value > this.max) {
                        value = this.max;
                    }
                    this._set(value);
                }
            },

            /**
             * Decrease the value to the next lower amount allowed by `step`. If `step` is undefined, do nothing.
             */
            decrement() {
                const step = this.step;
                if (step) {
                    let value = this.parse(this.$refs._in.value);
                    value = (Math.ceil(value / step - 1e-12) - 1) / (1 / step);
                    if (value < this.min) {
                        value = this.min;
                    }
                    this._set(value);
                }
            },

            /** @private */
            drop(e) {
                e.preventDefault();
            },

            /** @private */
            keydown(e) {
                if (!e.ctrlKey && !e.metaKey && ![0,8,9,13,16,17,18,19,20,33,34,35,36,37,38,39,40,46].includes(e.which)) {
                    this.saveState();
                }
                this.which = e.which;
                this.$emit('keydown', e);
            },

            /** @private */
            change() {
                const input = this.$refs._in;
                const value = this.toValue();
                const fval = this.format(value);
                if (fval != null && fval != input.value) {
                    input.value = fval;
                }
                this.$emit('change', value);
            },

            /**
             * Convert edited/displayed value to `this.value`.
             * @private
             */
            toValue() {
                const input = this.$refs._in;
                return input.value
                    ? this.valid
                        ? this.parse(input.value)
                        : NaN
                    : null;
            },

            /**
             * Save cursor position and value to restore in case of future invalid input.
             * @private
             */
            saveState() {
                const input = this.$refs._in;
                this.saveStart = input.selectionStart;
                this.saveEnd = input.selectionEnd;
                this.saveValue = input.value;
            },

            /**
             * Restore cursor position and value on invalid input.
             * @private
             */
            restoreState() {
                const input = this.$refs._in;
                if (input.value != this.saveValue) {
                    input.value = this.saveValue;
                    input.selectionStart = this.saveStart;
                    input.selectionEnd = this.saveEnd;
                }
            },

            /**
             * Custom validation, due to this component generally using `<input type="text">`
             * which does not support min/max/step validation in HTML5.
             * @private
             */
            validate() {
                let validity = null;
                const input = this.$refs._in;
                if (this.error) {
                    validity = this.error;
                } else if (!input.value) {
                    if (this.required) {
                        validity = (this.errorMissing || $locale.data.numMissing);
                    }
                } else {
                    const value = this.parse(input.value);
                    if (value < this.min) {
                        validity = (this.errorUnderflow || $locale.data.numMin).replace('{min}', this.formatter.format(this.min));
                    } else if (value > this.max) {
                        validity = (this.errorOverflow || $locale.data.numMax).replace('{max}', this.formatter.format(this.max));
                    } else if (this.stepInvalid(value)) {
                        validity = (this.errorInvalid || $locale.data.numStep).replace('{step}', this.formatter.format(this.step));
                    }
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return !validity;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<docs>
    Basic Example:
    ```jsx
    new Vue({
        data() {
            return {
                val: new Array(4)
            }
        },
        template:
            `<ib-context>
                <p><ib-field-numeric v-model="val[0]" placeholder="Default"/> Value: {{val[0]}}</p>
                <p><ib-field-numeric v-model="val[1]" placeholder=":decimal" decimal/> Value: {{val[1]}}</p>
                <p><ib-field-numeric v-model="val[2]" placeholder=":step=0.01" :step="0.01"/> Value: {{val[2]}}</p>
                <p><ib-field-numeric v-model="val[3]" placeholder=":min=0 :max=100" :min="0" :max="100"/> Value: {{val[3]}}</p>
            </ib-context>`
    })
    ```
</docs>
